import { useNavigate } from 'react-router-dom';
import { Settings, CreditCard, LogOut, Users } from 'lucide-react';
import { useAthleteStore } from '@/store';
import { useAuthStore } from '@/store/authStore';

interface ProfileHeaderProps {
    onOpenStats: () => void;
    stats: any; // Type from hook
}

export function ProfileHeader({ onOpenStats, stats }: ProfileHeaderProps) {
    const navigate = useNavigate();
    const { profile, logout } = useAuthStore();

    // Legacy mapping for users without a Supabase profile yet
    const athlete = useAthleteStore(s => s.athletes.find(a => a.id === s.activeId));

    // Mock Social Stats for now
    const socialStats = {
        posts: 42,
        followers: 1250,
        following: 380,
    };

    return (
        <div className="relative mb-12">
            {/* Premium Cover Photo Area */}
            <div className="h-48 md:h-64 w-full relative overflow-hidden rounded-b-3xl -mt-6 md:-mt-10 mb-16 md:mb-20">
                <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1599058945522-28d584b6f0ff?q=80&w=2069&auto=format&fit=crop')] bg-cover bg-center" />
                <div className="absolute inset-0 bg-gradient-to-t from-bg via-bg/50 to-transparent" />
                <div className="absolute inset-0 bg-black/20" />
            </div>

            <div className="flex flex-col md:flex-row gap-6 md:gap-10 items-start px-4 md:px-8 relative z-10 -mt-24 md:-mt-32">
                {/* Avatar Section */}
                <div className="flex-shrink-0 mx-auto md:mx-0 relative">
                    <div className="absolute inset-0 bg-gold-500/30 blur-xl rounded-full animate-pulse" />
                    <div className="relative w-32 h-32 md:w-40 md:h-40 rounded-full p-1 bg-gradient-to-tr from-gold-500 via-gold-300 to-bg-surface2 shadow-xl">
                        <div className="w-full h-full rounded-full border-[3px] border-bg bg-bg-elevated overflow-hidden">
                            {profile?.avatar || athlete?.avatar ? (
                                <img src={profile?.avatar || athlete?.avatar} alt="Profile" className="w-full h-full object-cover" />
                            ) : (
                                <div className="w-full h-full flex items-center justify-center bg-bg-surface2 text-3xl font-bold text-text-muted">
                                    {profile?.name?.[0] || athlete?.name?.[0] || 'U'}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Info Section */}
                <div className="flex-1 w-full text-center md:text-left mt-4 md:mt-12 backdrop-blur-sm bg-bg-surface/30 p-6 rounded-3xl border border-white/5 shadow-2xl">
                    {/* Header Top: Name & Actions */}
                    <div className="flex flex-col md:flex-row justify-between items-center md:items-start gap-4 mb-6">
                        <div className="flex flex-col">
                            <h1 className="text-2xl md:text-3xl font-black text-white tracking-tight flex items-center justify-center md:justify-start gap-2 drop-shadow-md">
                                {profile?.name || athlete?.name || 'Wrestler'}
                                <span className="text-blue-500" title="Verified Athlete">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M10.59 13.41L7.41 10.23L6 11.64L10.59 16.23L18 8.82L16.59 7.41L10.59 13.41Z" /></svg>
                                </span>
                            </h1>
                            {profile?.team && (
                                <div className="flex items-center justify-center md:justify-start gap-3 mt-1">
                                    <div className="text-sm font-bold text-gold-500 uppercase tracking-wide">
                                        {profile.team}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="flex gap-2">
                            <button
                                onClick={() => navigate('/profile/edit')}
                                className="px-5 py-2 text-sm font-bold rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-white transition-all hover:scale-105 active:scale-95"
                            >
                                Edit Profile
                            </button>
                            <button
                                onClick={onOpenStats}
                                className="px-5 py-2 text-sm font-bold rounded-xl bg-gradient-to-r from-gold-600 to-gold-400 text-bg-900 flex items-center gap-2 group hover:shadow-[0_0_20px_rgba(212,175,55,0.4)] transition-all hover:scale-105 active:scale-95"
                            >
                                <CreditCard size={16} className="group-hover:rotate-12 transition-transform duration-300" />
                                View Card
                            </button>
                            <button
                                onClick={() => navigate('/profiles')}
                                className="p-2 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-text-muted hover:text-white transition-all"
                                title="Switch Profile"
                            >
                                <Users size={18} />
                            </button>
                            <button
                                onClick={() => navigate('/settings')}
                                className="p-2 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-text-muted hover:text-white transition-all hover:rotate-90"
                                title="Settings"
                            >
                                <Settings size={18} />
                            </button>
                            <button
                                onClick={logout}
                                className="p-2 rounded-xl bg-red-500/5 hover:bg-red-500/10 border border-red-500/10 text-red-500/50 hover:text-red-500 transition-all"
                                title="Logout"
                            >
                                <LogOut size={18} />
                            </button>
                        </div>
                    </div>

                    {/* Stats Row */}
                    <div className="flex justify-center md:justify-start gap-8 mb-6 text-sm md:text-base bg-black/20 p-4 rounded-2xl border border-white/5 backdrop-blur-md">
                        <div className="text-center">
                            <div className="text-xl font-black text-white">{socialStats.posts}</div>
                            <div className="text-[10px] font-bold text-text-muted uppercase tracking-wider">Posts</div>
                        </div>
                        <div className="w-px bg-white/10" />
                        <div className="text-center">
                            <div className="text-xl font-black text-white">{socialStats.followers}</div>
                            <div className="text-[10px] font-bold text-text-muted uppercase tracking-wider">Followers</div>
                        </div>
                        <div className="w-px bg-white/10" />
                        <div className="text-center">
                            <div className="text-xl font-black text-white">{socialStats.following}</div>
                            <div className="text-[10px] font-bold text-text-muted uppercase tracking-wider">Following</div>
                        </div>
                    </div>

                    {/* Bio Section */}
                    <div className="text-sm md:text-base space-y-2">
                        <div className="inline-block px-3 py-1 rounded-full bg-gold-500/10 border border-gold-500/20 text-gold-400 text-xs font-bold uppercase tracking-wider mb-1">
                            {stats.archetype || 'Wrestler'}
                        </div>
                        <div className="text-text-secondary whitespace-pre-wrap leading-relaxed">
                            Global Elite 🤼‍♂️
                            <br />State Champ '24 🥇
                            <br /><span className="italic">"Hard work beats talent."</span>
                        </div>
                        <a href="#" className="text-blue-400 hover:text-blue-300 transition-colors font-medium flex items-center gap-1 mt-2 text-sm max-w-fit bg-blue-500/10 px-3 py-1.5 rounded-lg border border-blue-500/20">
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>
                            palaestra.app
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
}
